import { useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import { getBuildingTypes, setBuildingType } from './Api';

// Company user marks which buildings are townhouses. Buildings saved with no
// type show "Not set" here; Edit Property used to display them as Apartment.
const BuildingTypesModal = ({ isOpen, onClose, onChanged }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    getBuildingTypes()
      .then(setProperties)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleChange = async (propertyId, buildingName, type) => {
    const key = `${propertyId}|${buildingName}`;
    setSavingKey(key);
    try {
      const result = await setBuildingType(propertyId, buildingName, type);
      setProperties(list => list.map(p => (p.property_id !== propertyId ? p : {
        ...p,
        buildings: p.buildings.map(b => (b.name === buildingName ? { ...b, type } : b)),
      })));
      toast.success(result?.message || 'Building type updated');
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingKey('');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered scrollable>
      <ModalHeader toggle={onClose}>Townhouse Buildings</ModalHeader>
      <ModalBody>
        <p className="text-muted">
          The tracker lists units of buildings set to <strong>Townhouse</strong>. The same setting is the
          Building Type in Edit Property.
        </p>
        {loading ? (
          <div className="text-center py-4"><Spinner /></div>
        ) : !properties.length ? (
          <Alert color="info">No active properties found.</Alert>
        ) : (
          <Table size="sm" className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Property</th>
                <th>Building</th>
                <th style={{ width: 180 }}>Type</th>
              </tr>
            </thead>
            <tbody>
              {properties.map(p => (p.buildings.length ? p.buildings.map((b, i) => {
                const key = `${p.property_id}|${b.name}`;
                return (
                  <tr key={key}>
                    <td>{i === 0 ? p.title : ''}</td>
                    <td>{b.name}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={b.type}
                          disabled={savingKey === key}
                          onChange={e => handleChange(p.property_id, b.name, e.target.value)}
                        >
                          {!b.type && <option value="">Not set</option>}
                          <option value="Apartment">Apartment</option>
                          <option value="Townhouse">Townhouse</option>
                        </Input>
                        {savingKey === key && <Spinner size="sm" />}
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr key={p.property_id}>
                  <td>{p.title}</td>
                  <td colSpan={2} className="text-muted">
                    No building added. Add one in Edit Property first.
                  </td>
                </tr>
              )))}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>Done</Button>
      </ModalFooter>
    </Modal>
  );
};

export { BuildingTypesModal };
